import React from "react";
import {
  MdOutlineTimer,
  MdOutlineCheckCircle,
  MdOutlineHourglassEmpty,
  MdOutlineEventBusy,
  MdOutlineNightlight,
} from "react-icons/md";
import { HiOutlineUsers } from "react-icons/hi2";
import { formatHours } from "Utils/utils";
import TimesheetKpiCard, { TIMESHEET_KPI_GRID_CLASS } from "./TimesheetKpiCard";
import {
  formatKpiCount,
  formatKpiHoursRatio,
  formatKpiRatio,
} from "./timesheetKpiFormat";

const TimesheetKpiStrip = ({ summary, loading = false }) => {
  const showSkeleton = loading && !summary;

  if (showSkeleton) {
    return (
      <div
        className={TIMESHEET_KPI_GRID_CLASS}
        aria-label="Timesheet summary"
        aria-busy="true"
      >
        {Array.from({ length: 6 }).map((_, index) => (
          <TimesheetKpiCard key={index} skeleton />
        ))}
      </div>
    );
  }

  const approved = summary?.approved_time_sheet ?? 0;
  const pending = summary?.pending_time_sheet ?? 0;
  const expired = summary?.expired_time_sheet ?? 0;
  const total = summary?.total_time_sheet ?? 0;

  const totalHours = summary?.total_time_sheet_hours;
  const approvedHours = summary?.approved_time_sheet_hours;

  const totalSleepovers = summary?.total_sleepover_count ?? 0;
  const approvedSleepovers = summary?.approved_sleepover_count ?? 0;

  const workersWithPending = summary?.workers_with_pending ?? 0;
  const workersInScope = summary?.workers_in_scope ?? 0;

  const approvedPct =
    total > 0 ? Math.round((approved / total) * 100) : 0;
  const pendingPct =
    total > 0 ? Math.round((pending / total) * 100) : 0;
  const expiredPct =
    total > 0 ? Math.round((expired / total) * 100) : 0;

  const hoursValue = formatKpiHoursRatio(approvedHours, totalHours);
  const hoursValueTitle = `${formatHours(approvedHours) || "0:00"} / ${formatHours(totalHours) || "0:00"} approved of total`;

  const sleepoverValue = formatKpiRatio(approvedSleepovers, totalSleepovers);
  const refreshing = loading && Boolean(summary);

  return (
    <div
      className={TIMESHEET_KPI_GRID_CLASS}
      aria-label="Timesheet summary"
      aria-busy={loading}
    >
      <TimesheetKpiCard
        label="Pending"
        value={formatKpiRatio(pending, total)}
        unit="timesheets"
        valueTitle={`${formatKpiCount(pending)} of ${formatKpiCount(total)} pending timesheets`}
        caption={
          total > 0
            ? `${pendingPct}% awaiting your review`
            : "Awaiting your review"
        }
        icon={MdOutlineTimer}
        tone="pending"
        refreshing={refreshing}
      />
      <TimesheetKpiCard
        label="Approved"
        value={formatKpiRatio(approved, total)}
        unit="approved"
        valueTitle={`${formatKpiCount(approved)} of ${formatKpiCount(total)} approved`}
        caption={`${approvedPct}% of period complete`}
        icon={MdOutlineCheckCircle}
        tone="approve"
        refreshing={refreshing}
      />
      <TimesheetKpiCard
        label="Expired"
        value={formatKpiRatio(expired, total)}
        unit="timesheets"
        valueTitle={`${formatKpiCount(expired)} of ${formatKpiCount(total)} expired timesheets`}
        caption={
          total > 0
            ? `${expiredPct}% past submission window`
            : "Past submission window"
        }
        icon={MdOutlineEventBusy}
        tone="expired"
        refreshing={refreshing}
      />
      <TimesheetKpiCard
        label="Total hours"
        value={hoursValue}
        unit="hours"
        valueTitle={hoursValueTitle}
        caption="Approved / total hours in period"
        captionTitle="Approved hours of total logged in period"
        icon={MdOutlineHourglassEmpty}
        tone="brand"
        refreshing={refreshing}
      />
      <TimesheetKpiCard
        label="Sleepover"
        value={sleepoverValue}
        unit="sleepovers"
        valueTitle={`${formatKpiCount(approvedSleepovers)} of ${formatKpiCount(totalSleepovers)} sleepovers`}
        caption="Approved / total sleepovers"
        captionTitle="Approved sleepovers of total in period"
        icon={MdOutlineNightlight}
        tone="brand"
        refreshing={refreshing}
      />
      <TimesheetKpiCard
        label="Workers with pending"
        value={formatKpiCount(workersWithPending)}
        unit={`of ${formatKpiCount(workersInScope)}`}
        valueTitle={`${formatKpiCount(workersWithPending)} workers with pending of ${formatKpiCount(workersInScope)} in scope`}
        caption="Have shifts to review"
        icon={HiOutlineUsers}
        tone="muted"
        refreshing={refreshing}
      />
    </div>
  );
};

export default TimesheetKpiStrip;
